import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Button, HStack} from "@chakra-ui/react";

import objectUtil from "@/utils/objectUtil";
import {Skeleton} from "@/components";
import {useLanguage} from "@/contexts/languageContext";

export const PaginatorSkeleton = ({maxVisiblePages = 5}) => {
    return <HStack gap=".5rem">
        {
            Array.from({length: maxVisiblePages}).map((e, i) =>
                <Skeleton key={`paginator-${i}`} h="2.5rem" w="2.5rem"/>
            )
        }
    </HStack>
}

export const Paginator = ({defaultPage, setGetParams, itemNumber, perPage = 20, maxVisiblePages = 5}) => {

    const { t } = useLanguage();
    const isMounted = useRef(false);
    const [page, setPage] = useState(defaultPage);

    /** Updating URL on page change to make is stateful on reload */
    useEffect(() => {
        if(isMounted.current){
            setGetParams((prevState) => ({
                ...objectUtil.filterByKeys(prevState, ['page'], false),
                ...(page > 0 ? { page } : {})
            }))
        } else {
            isMounted.current = true;
        }
    }, [page]);

    const maxPage = useMemo(() => {
        return Math.ceil(itemNumber / perPage);
    }, [itemNumber, perPage])

    const goToPage = useCallback(async (givenPage) => {
        givenPage = Math.max(0, Math.min(maxPage, givenPage));
        setPage(() => givenPage);
    }, [page]);

    const paginationPages = useMemo(() => {
        const visiblePages = Math.min(maxVisiblePages, maxPage);

        let minimum = Math.max(0, page - 2);
        let maximum = minimum + visiblePages;

        if (maximum > maxPage) {
            maximum = maxPage;
            minimum = Math.max(0, maxPage - visiblePages);
        }

        return Array.from({length: maximum - minimum}).map((_, i) => minimum + i);
    }, [page, maxVisiblePages, maxPage]);

    return useMemo(() => {
        return (<HStack gap=".5rem">
            {
                paginationPages.map((i) => (
                    <Button
                        key={`pagination-btn-${i}`}
                        onClick={() => goToPage(i)}
                        bg={i === page ? 'secondary.500' : 'white'}
                        color={i === page ? 'white' : 'secondary.500'}
                        size={i === page ? 'lg' : 'md'}
                        aria-label={t('sentences.goToPage', { value: i})}
                    >
                        {i + 1}
                    </Button>
                ))
            }
        </HStack>)
    }, [paginationPages]);
};
